// https://github.schibsted.io/finn/troika-react/tree/master/packages/slider
// This file exists because the package in the above url is deprecated
// and doesn't work properly anymore.
//
// This file should be removed when Troika is replaced with Fabric

import {
    type KeyboardEvent,
    type RefObject,
    useCallback,
    useEffect,
    useLayoutEffect as useLayoutEffectSafely,
    useRef,
    useState,
} from 'react';

import classNames from 'classnames';
import { animated, to, useSpring } from 'react-spring';
import { useDrag } from 'react-use-gesture';

import ResizeObserver from 'resize-observer-polyfill';

/**
 * Hook for determining the size of an element using the Resize Observer API.
 *
 * @param ref - A React ref to an element
 */
function useElementSizeObserver(ref: RefObject<Element>): { width: number; height: number } {
    const [size, setSize] = useState({ width: 0, height: 0 });

    const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
        setSize({
            // we only observe one element, so accessing the first entry here is fine
            width: entries[0].contentRect.width,
            height: entries[0].contentRect.height,
        });
    }, []);

    useLayoutEffect(() => {
        if (!ref.current) {
            return;
        }

        // Set initial size here, as the one from the observer fires too late on iOS safari
        const { width, height } = ref.current.getBoundingClientRect();
        setSize({ width, height });

        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(ref.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [ref, handleResize]);

    return size;
}

// By deducting the handle width we lock the handle to edges of the slider
function useInnerWidth(slider: RefObject<HTMLDivElement>, handle: RefObject<HTMLDivElement>): number {
    const { width } = useElementSizeObserver(slider);
    const [innerWidth, setInnerWidth] = useState(width);

    useLayoutEffect(() => {
        setInnerWidth(width - (handle.current?.offsetWidth ?? 0));
    }, [width, handle]);

    return innerWidth;
}

// a d3 scale like object.
export interface Scale {
    (value: number): number;
    invertExtent(value: number): [number, number];
    thresholds(): number[];
}

/**
 * Restricts the value to the given range.
 */
export function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

export function valueToRatio(value: number, min: number, max: number, scale?: Scale): number {
    if (scale) {
        // if we are above the middle, use the upper part of the segment
        const extent = (max - min) / 2;
        return scale.invertExtent(value)[value > extent ? 1 : 0];
    }

    return clamp((value - min) / (max - min), 0, 1);
}

export function ratioToValue(ratio: number, min: number, max: number, step: number, scale?: Scale): number {
    if (scale) {
        return scale(ratio);
    }

    let value = (max - min) * ratio;

    value = Math.round(value / step) * step + min;
    return clamp(min, value, max);
}

export function nextValue(value: number, step: number, scale?: Scale): number {
    if (!scale) {
        return value + step;
    }

    const extent = scale.invertExtent(value)[1];

    const next = scale.thresholds().find((v) => v >= extent) ?? 1;
    return scale(next);
}

export function prevValue(value: number, step: number, scale?: Scale): number {
    if (!scale) {
        return value - step;
    }

    const extent = scale.invertExtent(value)[0];

    const prev =
        scale
            .thresholds()
            .reverse()
            .find((v) => v < extent) ?? 0;
    return scale(prev);
}

const useLayoutEffect = typeof window !== 'undefined' ? useLayoutEffectSafely : useEffect;
export type RangeSliderProps = {
    /** String value that labels the slider. */
    'aria-label'?: [string, string];

    /** Identifies the element that labels the slider. */
    'aria-labelledby'?: [string, string];

    /** Human readable text alternative for the value. */
    'aria-valuetext'?: [string, string];

    /** Additional CSS class for the container. */
    className?: string;

    /** Whether the slider is disabled. */
    disabled?: boolean;

    /** Handler that is called every time the value of the slider changes. */
    onInput?: (value: [number, number]) => void;

    /** Handler that is called when the value of the slider has settled. */
    onChange?: (value: [number, number]) => void;

    /** The greatest value in the range of permitted values. */
    max?: number;

    /** The lowest value in the range of permitted values. */
    min?: number;

    /** A d3-scale object for non linear slider scales.
     * @see d3-scale repository https://github.com/d3/d3-scale
     */
    scale?: Scale;

    /** Specifies the value granularity. */
    step?: number;

    /** The current value */
    value: [number, number];
};

const LOWER_VALUE = 0;
const UPPER_VALUE = 1;

type Handle = typeof LOWER_VALUE | typeof UPPER_VALUE;

export const RangeSlider = ({
    className,
    disabled = false,
    value: values,
    onInput = () => {},
    onChange = () => {},
    max = 100,
    min = 0,
    scale,
    step = 1,
    ...props
}: RangeSliderProps) => {
    const sliderRef = useRef<HTMLDivElement>(null);
    const handleLowerRef = useRef<HTMLDivElement>(null);
    const handleUpperRef = useRef<HTMLDivElement>(null);
    const innerWidth = useInnerWidth(sliderRef, handleLowerRef);
    /* Ref that is continually upated with the latest value as the slider is dragged */
    const internalValue = useRef([...values]);
    /* If we are currently dragging the slider */
    const [isDragging, setIsDragging] = useState(false);

    // the value should already be clamped
    const handleChange = (value: number, handle: Handle) => {
        if (values[handle] !== value) {
            const newValues = values.concat() as [number, number];

            newValues[handle] = value;
            onChange(newValues);
        }
    };

    const handleKeyDown = (event: KeyboardEvent, handle: Handle = LOWER_VALUE) => {
        if (disabled) return;

        const oldValue = values[handle];
        let newValue: number;
        switch (event.key) {
            case 'ArrowLeft':
            case 'ArrowDown':
            case 'PageDown':
                newValue = prevValue(oldValue, step, scale);
                break;

            case 'ArrowUp':
            case 'ArrowRight':
            case 'PageUp':
                newValue = nextValue(oldValue, step, scale);
                break;
            case 'Home':
                newValue = min;
                break;
            case 'End':
                newValue = max;
                break;
        }

        if (newValue != null) {
            newValue = clamp(
                newValue,
                handle === LOWER_VALUE ? min : values[LOWER_VALUE],
                handle === UPPER_VALUE ? max : values[UPPER_VALUE],
            );
            const newValues = values.concat() as [number, number];
            newValues[handle] = newValue;
            onInput(newValues);
            handleChange(newValue, handle);
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const [spring, set] = useSpring<{ ratioLower: number; ratioUpper: number }>(() => ({
        ratioLower: valueToRatio(values[LOWER_VALUE], min, max, scale),
        ratioUpper: valueToRatio(values[UPPER_VALUE], min, max, scale),
        immediate: true,
    }));

    // We only want to update the ratio for the spring if the value is something other than what we last dragged the handle to
    // this prevents the handle from jumping a couple of pixels even though we are really displaying the same value.
    useEffect(() => {
        if (internalValue.current[LOWER_VALUE] !== values[LOWER_VALUE]) {
            internalValue.current[LOWER_VALUE] = values[LOWER_VALUE];
            set({
                ratioLower: valueToRatio(values[LOWER_VALUE], min, max, scale),
            });
        }
        if (internalValue.current[UPPER_VALUE] !== values[UPPER_VALUE]) {
            internalValue.current[UPPER_VALUE] = values[UPPER_VALUE];
            set({
                ratioUpper: valueToRatio(values[UPPER_VALUE], min, max, scale),
            });
        }
    }, [values, set, max, min, scale]);

    const bind = useDrag(
        ({
            xy,
            first,
            last,
            memo = {
                rect: sliderRef.current?.getBoundingClientRect(),
                handle: LOWER_VALUE,
            },
        }) => {
            let ratio = clamp((xy[0] - memo.rect.left) / memo.rect.width, 0, 1);

            const ratioLower = spring.ratioLower.get();
            const ratioUpper = spring.ratioUpper.get();

            // Figure out which handle we're closest to
            if (first) {
                let handle: Handle;
                // if both handles are maxed out, we always use the lower handle  so we have something to interact with
                if (values[LOWER_VALUE] === max) {
                    handle = LOWER_VALUE;
                    // opposite of above
                } else if (values[UPPER_VALUE] === min) {
                    handle = UPPER_VALUE;
                    // otherwise, detect which handle is closest to the click/press
                } else {
                    handle =
                        Math.abs(ratioLower - ratio) <
                        // add a tiny fraction to the upper ratio when calculating which handle to interact with.
                        // This is because when the ratios are equal it would always choose the upper handle,
                        // because the check is less than. With our little padding however it can select either
                        Math.abs(ratioUpper + 0.0001 - ratio)
                            ? LOWER_VALUE
                            : UPPER_VALUE;
                }
                memo.handle = handle;

                setIsDragging(true);
            }

            // clamp the ratio with each other, as we don't want the handles to overlap
            ratio = memo.handle === LOWER_VALUE ? clamp(ratio, 0, ratioUpper) : clamp(ratio, ratioLower, 1);

            const dragValue = ratioToValue(ratio, min, max, step, scale);

            if (dragValue !== internalValue.current[memo.handle]) {
                internalValue.current[memo.handle] = dragValue;
                onInput(internalValue.current.concat() as [number, number]);
            }

            if (last) {
                setIsDragging(false);
                handleChange(dragValue, memo.handle);
                const ref = memo.handle === LOWER_VALUE ? handleLowerRef : handleUpperRef;
                ref.current?.focus();
            }

            set({
                [memo.handle === LOWER_VALUE ? 'ratioLower' : 'ratioUpper']: ratio,
            });

            return memo;
        },
        { axis: 'x', enabled: !disabled },
    );

    return (
        <div
            {...bind()}
            data-body-scroll-lock-ignore
            className={classNames('slider', { 'slider--is-disabled': disabled }, className)}
            style={{ cursor: isDragging ? 'grabbing' : 'pointer' }}
            data-testid={props['data-testid'] || ''}
            ref={sliderRef}>
            <div className="slider__track-inactive" />
            <animated.div
                className="slider__track-active"
                style={{
                    left: spring.ratioLower.to((ratio) => `${ratio * 100}%`),
                    right: spring.ratioUpper.to((ratio) => `${(1 - ratio) * 100}%`),
                }}
            />
            <animated.div
                aria-disabled={disabled}
                aria-label={props['aria-label']?.[LOWER_VALUE]}
                aria-labelledby={props['aria-labelledby']?.[LOWER_VALUE]}
                // the lower handle is limited by the upper handle
                aria-valuemax={values[UPPER_VALUE]}
                aria-valuemin={min}
                aria-valuenow={spring.ratioLower.to((ratio) => ratioToValue(ratio, min, max, step, scale))}
                aria-valuetext={props['aria-valuetext']?.[LOWER_VALUE]}
                className="slider__thumb"
                onKeyDown={handleKeyDown}
                role="slider"
                ref={handleLowerRef}
                style={{
                    transform: to(
                        [spring.ratioLower, spring.ratioUpper],
                        (ratioLower, ratioUpper) => `translate3d(${clamp(ratioLower, 0, ratioUpper) * innerWidth}px, 0px, 0px)`,
                    ),
                    // override troika's styling here
                    cursor: 'inherit',
                }}
                tabIndex={disabled ? undefined : 0}
            />
            <animated.div
                aria-disabled={disabled}
                aria-label={props['aria-label']?.[UPPER_VALUE]}
                aria-labelledby={props['aria-labelledby']?.[UPPER_VALUE]}
                aria-valuemax={max}
                // the upper handle is limited by the lower handle
                aria-valuemin={values[LOWER_VALUE]}
                aria-valuenow={spring.ratioUpper.to((ratio) => ratioToValue(ratio, min, max, step, scale))}
                aria-valuetext={props['aria-valuetext']?.[UPPER_VALUE]}
                className="slider__thumb"
                onKeyDown={(event) => handleKeyDown(event, UPPER_VALUE)}
                role="slider"
                ref={handleUpperRef}
                style={{
                    transform: to(
                        [spring.ratioLower, spring.ratioUpper],
                        (ratioLower, ratioUpper) => `translate3d(${clamp(ratioUpper, ratioLower, 1) * innerWidth}px, 0px, 0px)`,
                    ),
                    // override troika's styling here
                    cursor: 'inherit',
                }}
                tabIndex={disabled ? undefined : 0}
            />
        </div>
    );
};

const WorkaroundSlider = (props: RangeSliderProps) => {
    const [mounted, setMounted] = useState(false);
    useLayoutEffect(() => {
        setMounted(true);
    }, []);

    if (mounted) {
        return <RangeSlider {...props} />;
    }

    return (
        <div className={classNames('slider', props.className)}>
            <div className="slider__track-inactive" />
        </div>
    );
};

export default WorkaroundSlider;
